import { memo } from 'react';

import { ArrowBack, LockFat } from 'icons';
import { FormattedMessage } from 'localization';
import { twMerge } from 'tailwind-merge';

import { Button } from '../../../atoms';

interface IConnectCMSSection {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  logo?: string;
  logoClassName?: string;
  logoElement?: React.ReactNode;
  className?: string;
  isActive?: boolean;
  onClick?: () => void;
  onUpgradeClick?: () => void;
  actionButton?: React.ReactNode;
  upgradeNeeded?: boolean;
  id?: string;
}

const _ConnectCMSSection = ({
  title,
  subtitle,
  logo,
  className,
  logoClassName,
  isActive,
  logoElement,
  onClick,
  onUpgradeClick,
  actionButton,
  upgradeNeeded,
  id,
}: IConnectCMSSection) => {
  return (
    <div className="relative">
      <div
        id={id}
        onClick={onClick}
        className={twMerge(
          'group flex items-center gap-4 border border-v2blueGray-200 w-full h-[84px] px-6 rounded-[14px]',
          onClick && 'hover:cursor-pointer',
          upgradeNeeded && 'pointer-events-none select-none ',
          className,
        )}
      >
        {logo && (
          <img src={logo} alt="logo" className={twMerge('w-[32px] h-[32px]', logoClassName)} />
        )}
        {logoElement && logoElement}
        <div className="flex flex-col flex-1">
          <span className="m-0 text-v2blueGray-800 text-[14px] tracking-[-0.252px] font-medium">
            {title}
          </span>
          {(subtitle || upgradeNeeded) && (
            <span className="m-0 mt-[2px] text-v2blueGray-500 font-normal text-[12px] tracking-[-0.144px]">
              {upgradeNeeded ? <FormattedMessage id="cms.general.locked" /> : subtitle}
            </span>
          )}
        </div>

        {actionButton && !upgradeNeeded && <div className="relative">{actionButton}</div>}

        {!actionButton && !upgradeNeeded && (
          <div
            className={twMerge(
              'w-9 h-9 flex justify-center items-center group-hover:bg-v2blueGray-100 rounded-md',
              isActive && 'group-hover:bg-primary/20',
            )}
          >
            <div className="transition duration-300 transform rotate rotate-180 group-hover:rotate-[135deg]">
              <ArrowBack
                width={24}
                height={24}
                className={isActive ? 'text-primary' : 'text-v2blueGray-500'}
              />
            </div>
          </div>
        )}
      </div>

      {upgradeNeeded && (
        <div className="p-[23px] absolute bg-white/50 top-0 left-0 w-full h-full rounded-[14px] flex items-center justify-end">
          <Button icon={<LockFat width={24} height={24} />} onClick={onUpgradeClick}>
            <FormattedMessage id="general.upgrade" />
          </Button>
        </div>
      )}
    </div>
  );
};

const ConnectCMSSection = memo<IConnectCMSSection>(_ConnectCMSSection);

ConnectCMSSection.displayName = 'ConnectCMSSection';

export { ConnectCMSSection };
